/*
 * Blackout Audio: wishlist, stock reminders and sale reminders.
 *
 * Members: the customer metafields are the source of truth (custom.wishlist,
 * custom.stock_notifications, custom.sale_notifications), written by the
 * ba-wishlist-integration worker behind the App Proxy (/apps/wishlist).
 * Liquid renders the first state; this script handles clicks, optimistic UI
 * and the drawer.
 *
 * Guests: the heart saves in this browser (localStorage), and the list moves
 * into the account at log in (POST /merge). The first save shows a note under
 * the header heart, once per browser, that logging in keeps the list on any
 * device. Reminders send email, so the bell still sends guests to log in.
 *
 * Buttons opt in with:
 *   data-wishlist-btn data-list="wishlist" | "stock" | "sale" | "deal"
 *   data-product-id, data-product-title, data-product-url, data-product-image, data-product-price
 * and show state with .is-active (server-rendered on first paint).
 *
 * The product page bell (snippets/pdp-save-actions.liquid) is a
 * [data-remind] wrapper: [data-remind-toggle] opens its options on tap, and
 * .is-set marks that at least one reminder is on.
 *
 * window.BAWishlist (layout/theme.liquid):
 *   { loggedIn, customerId, loginUrl, proxyBase, wishlistEnabled, remindersEnabled }
 */
(function () {
  'use strict';

  var CFG = window.BAWishlist || {};
  var PROXY_BASE = CFG.proxyBase || '/apps/wishlist';

  // Recent clicks, so a cached page (back button) doesn't show stale state.
  // Keyed per customer: a different login on the same browser starts clean.
  var MIRROR_KEY = 'ba_wishlist_mirror_v2_' + (CFG.customerId || 'guest');

  function readMirror() {
    try { return JSON.parse(sessionStorage.getItem(MIRROR_KEY)) || {}; } catch (e) { return {}; }
  }

  function mirrorSet(list, productId, active) {
    var m = readMirror();
    if (!m[list]) m[list] = {};
    m[list][productId] = active;
    try { sessionStorage.setItem(MIRROR_KEY, JSON.stringify(m)); } catch (e) { /* ignore */ }
  }

  function mirrorGet(list, productId) {
    var m = readMirror();
    return m[list] && Object.prototype.hasOwnProperty.call(m[list], productId) ? m[list][productId] : null;
  }

  function loginUrl() {
    var path = window.location.pathname + window.location.search;
    return '/customer_authentication/login?return_to=' + encodeURIComponent(path);
  }

  function redirectToLogin() {
    window.location.href = loginUrl();
  }

  // A guest's bell press survives the login trip: counted now, and finished
  // (set) when they come back logged in.
  var INTENT_KEY = 'ba_wishlist_intent_v1';

  function postEvent(body) {
    return fetch(PROXY_BASE + '/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true
    }).catch(function () { /* tracking never blocks */ });
  }

  function guestClick(list, productId) {
    try { localStorage.setItem(INTENT_KEY, JSON.stringify({ list: list, productId: productId, at: Date.now() })); } catch (e) {}
    postEvent({ type: 'click', list: list, productId: productId }).then(redirectToLogin);
  }

  function finishIntent() {
    if (!CFG.loggedIn) return;
    var intent;
    try {
      intent = JSON.parse(localStorage.getItem(INTENT_KEY));
      localStorage.removeItem(INTENT_KEY);
    } catch (e) { return; }
    if (!intent || Date.now() - intent.at > 30 * 60 * 1000) return;
    postEvent({ type: 'login_return', list: intent.list, productId: intent.productId });
    var btn = buttonsFor(intent.list, intent.productId)[0];
    if (btn && !btn.classList.contains('is-active')) onToggleClick(btn);
  }

  /* ------------------------------ guests ------------------------------ */

  // A guest's wishlist, oldest first like the account list:
  // [{id, title, handle, image, price, at}].
  var GUEST_KEY = 'ba_wishlist_guest_v1';
  // Set when the log in note has shown; it never shows again in this browser.
  var HINT_KEY = 'ba_wishlist_hint_v1';
  // A random id per browser, so the dashboard can count browser lists.
  var BID_KEY = 'ba_wishlist_bid_v1';

  function readGuest() {
    try {
      var items = JSON.parse(localStorage.getItem(GUEST_KEY));
      return Array.isArray(items) ? items : [];
    } catch (e) { return []; }
  }

  function writeGuest(items) {
    try {
      if (items.length) localStorage.setItem(GUEST_KEY, JSON.stringify(items));
      else localStorage.removeItem(GUEST_KEY);
    } catch (e) { /* storage blocked: the heart still fills on this page */ }
  }

  function browserId() {
    var id = null;
    try { id = localStorage.getItem(BID_KEY); } catch (e) { /* none */ }
    if (!id) {
      id = window.crypto && crypto.randomUUID
        ? crypto.randomUUID()
        : 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
      try { localStorage.setItem(BID_KEY, id); } catch (e) { /* none */ }
    }
    return id;
  }

  // A drawer row from a heart's data attributes.
  function itemFromButton(btn, productId) {
    var after = (btn.getAttribute('data-product-url') || '').split('/products/')[1];
    return {
      id: String(productId),
      title: btn.getAttribute('data-product-title') || '',
      handle: after ? after.split(/[?#]/)[0] : '',
      image: btn.getAttribute('data-product-image') || '',
      price: btn.getAttribute('data-product-price') || '',
      at: Date.now()
    };
  }

  function guestState() {
    return { wishlist: readGuest(), stock: [], sale: [], deal: [] };
  }

  // Liquid renders a guest's hearts empty: fill the ones saved in this browser.
  function paintGuest() {
    if (CFG.loggedIn) return;
    var saved = {};
    readGuest().forEach(function (it) { saved[String(it.id)] = true; });
    document.querySelectorAll('[data-wishlist-btn][data-list="wishlist"]').forEach(function (b) {
      setActive(b, !!saved[b.getAttribute('data-product-id')]);
    });
  }

  // Cards added after load (filters, more products, recommendations) come in
  // empty for a guest too.
  function watchGuestCards() {
    if (CFG.loggedIn || !window.MutationObserver) return;
    var queued = false;
    new MutationObserver(function (records) {
      if (queued) return;
      for (var i = 0; i < records.length; i++) {
        if (!records[i].addedNodes.length) continue;
        if (!readGuest().length) return;
        queued = true;
        requestAnimationFrame(function () { queued = false; paintGuest(); });
        return;
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function guestToggle(btn, productId) {
    var items = readGuest();
    var at = -1;
    for (var i = 0; i < items.length; i++) if (String(items[i].id) === String(productId)) at = i;
    var willBeActive = at === -1;
    if (willBeActive) items.push(itemFromButton(btn, productId));
    else items.splice(at, 1);
    writeGuest(items);
    applyState('wishlist', productId, willBeActive);
    showNote(btn, willBeActive ? 'Added to wishlist' : 'Removed from wishlist');
    bumpCount(willBeActive ? 1 : -1);
    state = guestState();
    if (drawerOpen()) renderDrawer();
    postEvent({ type: 'click', list: 'wishlist', productId: productId, on: willBeActive, bid: browserId() });
    if (willBeActive) showHint();
    document.dispatchEvent(new CustomEvent('wishlist:changed', {
      detail: { list: 'wishlist', productId: productId, active: willBeActive, source: btn }
    }));
  }

  // Logged in with hearts saved while logged out: they join the account. The
  // browser list is cleared only once the account holds them.
  function mergeGuestList() {
    if (!CFG.loggedIn) return;
    var items = readGuest();
    if (!items.length) return;
    fetch(PROXY_BASE + '/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productIds: items.map(function (it) { return it.id; }), bid: browserId() })
    }).then(function (res) {
      if (!res.ok) throw new Error('merge failed: ' + res.status);
      return res.json();
    }).then(function (data) {
      writeGuest([]);
      var held = {};
      (data.wishlist || []).forEach(function (id) { held[String(id)] = true; });
      items.forEach(function (it) { applyState('wishlist', it.id, !!held[String(it.id)]); });
      if (data.added && data.added.length) bumpCount(data.added.length);
      invalidateDrawer();
      if (drawerOpen()) loadDrawer(true);
    }).catch(function () { /* kept in the browser; the next page tries again */ });
  }

  // The first guest save: a note under the header heart that logging in keeps
  // the list on any device. Once per browser, never again.
  var hintEl = null;

  function showHint() {
    try {
      if (localStorage.getItem(HINT_KEY)) return;
      localStorage.setItem(HINT_KEY, '1');
    } catch (e) { return; }
    hintEl = document.createElement('div');
    hintEl.className = 'wishlist-hint';
    hintEl.setAttribute('role', 'status');
    hintEl.innerHTML =
      '<p class="wishlist-hint__text">Saved in this browser. ' +
        '<a class="wishlist-hint__link" href="' + esc(loginUrl()) + '" data-wishlist-login>Log in</a>' +
        ' to see your wishlist on any device.</p>' +
      '<button type="button" class="wishlist-hint__close" aria-label="Close">' +
        '<svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M15 5L5 15M5 5l10 10"/></svg>' +
      '</button>';
    document.body.appendChild(hintEl);
    hintEl.querySelector('.wishlist-hint__close').addEventListener('click', hideHint);
    placeHint();
    window.addEventListener('resize', placeHint);
    requestAnimationFrame(function () { if (hintEl) hintEl.classList.add('is-shown'); });
    setTimeout(hideHint, 10000);
  }

  // 8px below the sticky header, right edge in line with the header heart's,
  // 16px inside the screen.
  function placeHint() {
    if (!hintEl) return;
    var vw = document.documentElement.clientWidth;
    var heart = document.querySelector('.header__icon--wishlist');
    var bar = document.querySelector('.header-wrapper');
    var r = heart && heart.getBoundingClientRect();
    var top = 16;
    var right = 16;
    if (r && r.width && r.bottom > 0) {
      top = Math.max(r.bottom, bar ? bar.getBoundingClientRect().bottom : 0) + 8;
      right = Math.max(16, vw - r.right);
    }
    hintEl.style.top = Math.round(top) + 'px';
    hintEl.style.right = Math.round(right) + 'px';
    hintEl.style.maxWidth = Math.round(Math.min(320, vw - right - 16)) + 'px';
  }

  function hideHint() {
    var el = hintEl;
    if (!el) return;
    hintEl = null;
    window.removeEventListener('resize', placeHint);
    el.classList.remove('is-shown');
    setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 200);
  }

  function postToggle(list, productId) {
    return fetch(PROXY_BASE + '/toggle', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ list: list, productId: String(productId) })
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) {
          var err = new Error(data.error || ('toggle failed: ' + res.status));
          err.status = res.status;
          throw err;
        }
        return data;
      });
    });
  }

  /* ------------------------------ buttons ------------------------------ */

  function setActive(btn, active) {
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  function buttonsFor(list, productId) {
    return document.querySelectorAll('[data-wishlist-btn][data-list="' + list + '"][data-product-id="' + productId + '"]');
  }

  function refreshRemindSet() {
    document.querySelectorAll('[data-remind]').forEach(function (wrap) {
      wrap.classList.toggle('is-set', !!wrap.querySelector('[data-wishlist-btn].is-active'));
    });
  }

  function applyState(list, productId, active) {
    buttonsFor(list, productId).forEach(function (b) { setActive(b, active); });
    mirrorSet(list, productId, active);
    refreshRemindSet();
  }

  function onToggleClick(btn) {
    var list = btn.getAttribute('data-list') || 'wishlist';
    var productId = btn.getAttribute('data-product-id');
    if (!productId || btn.hasAttribute('data-busy')) return;
    if (!CFG.loggedIn) {
      if (list === 'wishlist') guestToggle(btn, productId);
      else guestClick(list, productId);
      return;
    }

    var willBeActive = !btn.classList.contains('is-active');
    applyState(list, productId, willBeActive);
    updateStateLocally(list, btn, productId, willBeActive);
    if (list === 'wishlist') showNote(btn, willBeActive ? 'Added to wishlist' : 'Removed from wishlist');
    if (list === 'wishlist') bumpCount(willBeActive ? 1 : -1);
    invalidateDrawer();

    btn.setAttribute('data-busy', '');
    postToggle(list, productId)
      .then(function (data) {
        if (typeof data.active === 'boolean' && data.active !== willBeActive) {
          applyState(list, productId, data.active);
          if (list === 'wishlist') bumpCount(data.active ? 1 : -1);
        }
        document.dispatchEvent(new CustomEvent('wishlist:changed', {
          detail: { list: list, productId: productId, active: data.active, source: btn }
        }));
      })
      .catch(function (err) {
        applyState(list, productId, !willBeActive);
        if (list === 'wishlist') bumpCount(willBeActive ? -1 : 1);
        // Logged out in another tab: the proxy no longer sends a customer id.
        if (err && err.status === 401) redirectToLogin();
      })
      .then(function () {
        btn.removeAttribute('data-busy');
        if (drawerOpen()) loadDrawer(true);
      });
  }

  // A short note above the product page heart (.save-circle) after a press.
  function showNote(btn, text) {
    if (!btn.classList.contains('save-circle')) return;
    var note = btn.querySelector('.save-note');
    if (!note) {
      note = document.createElement('span');
      note.className = 'save-note';
      note.setAttribute('role', 'status');
      btn.appendChild(note);
    }
    note.textContent = text;
    clearTimeout(note._t);
    requestAnimationFrame(function () { note.classList.add('is-shown'); });
    note._t = setTimeout(function () { note.classList.remove('is-shown'); }, 2500);
  }

  // The open drawer follows a click at once (from the page or its own ×),
  // before the server answers; the next /state read reconciles.
  function updateStateLocally(list, btn, productId, active) {
    if (!state) return;
    var items = state[list] || (state[list] = []);
    var at = -1;
    for (var i = 0; i < items.length; i++) if (String(items[i].id) === String(productId)) at = i;
    if (!active && at !== -1) items.splice(at, 1);
    if (active && at === -1) items.push(itemFromButton(btn, productId));
    if (drawerOpen()) renderDrawer();
  }

  function onClick(e) {
    // Log in links (the drawer's Reminders tab, the guest note) come back here.
    if (e.target.closest('[data-wishlist-login]')) {
      e.preventDefault();
      redirectToLogin();
      return;
    }
    var toggle = e.target.closest('[data-remind-toggle]');
    if (toggle) {
      e.preventDefault();
      var wrap = toggle.closest('[data-remind]');
      var open = !wrap.classList.contains('is-open');
      closeAllRemind(wrap);
      wrap.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }
    var btn = e.target.closest('[data-wishlist-btn]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      onToggleClick(btn);
      return;
    }
    if (!e.target.closest('[data-remind]')) closeAllRemind(null);
  }

  function closeAllRemind(except) {
    document.querySelectorAll('[data-remind].is-open').forEach(function (w) {
      if (w === except) return;
      w.classList.remove('is-open');
      var t = w.querySelector('[data-remind-toggle]');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }

  // Centre each reminder tooltip over its bell, 16px inside the screen at
  // most (--shift moves it right of the bell's right edge, never left).
  function layoutReminders() {
    var vw = document.documentElement.clientWidth;
    document.querySelectorAll('[data-remind]').forEach(function (wrap) {
      var bell = wrap.querySelector('[data-remind-toggle]');
      var panel = wrap.querySelector('.save-remind__panel');
      if (!bell || !panel || !bell.offsetWidth) return;
      var b = bell.getBoundingClientRect();
      var pw = panel.offsetWidth;
      var shift = pw / 2 - b.width / 2;
      shift = Math.min(shift, vw - 16 - b.right);
      shift = Math.max(shift, 16 + pw - b.right);
      wrap.style.setProperty('--shift', Math.round(Math.max(0, shift)) + 'px');
    });
  }

  // A page restored from the back/forward cache may predate the last click.
  function reconcile() {
    if (!CFG.loggedIn) { paintGuest(); return; }
    document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      var m = mirrorGet(btn.getAttribute('data-list') || 'wishlist', btn.getAttribute('data-product-id'));
      if (m !== null) setActive(btn, m);
    });
    refreshRemindSet();
  }

  /* ------------------------------ header count ------------------------------ */

  function bumpCount(delta) {
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      setCount(el, Math.max(0, (parseInt(el.textContent, 10) || 0) + delta));
    });
  }

  function setCount(el, n) {
    el.textContent = n;
    var bubble = el.closest('.wishlist-count-bubble') || el;
    bubble.hidden = n === 0;
  }

  /* ------------------------------ drawer ------------------------------ */

  var state = null; // last /state response
  var stateAt = 0;

  function drawerEl() { return document.getElementById('WishlistDrawer'); }
  function drawerOpen() { var d = drawerEl(); return !!(d && d.classList.contains('is-open')); }
  function invalidateDrawer() { stateAt = 0; }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function money(amount) {
    var n = Number(amount);
    if (!isFinite(n)) return '';
    return '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function itemHtml(it, list, kindLabel) {
    var img = it.image
      ? '<img src="' + esc(it.image + (it.image.indexOf('?') === -1 ? '?' : '&') + 'width=160') + '" alt="' + esc(it.imageAlt || it.title) + '" loading="lazy" width="64" height="64">'
      : '';
    var url = '/products/' + it.handle;
    // Optimistic rows carry the page's already formatted price ("$449.00").
    var price = typeof it.price === 'string' && it.price.charAt(0) === '$' ? it.price : money(it.price);
    return (
      '<li class="wishlist-drawer__item" data-key="' + list + ':' + esc(it.id) + '">' +
        '<a class="wishlist-drawer__item-media" href="' + esc(url) + '">' + img + '</a>' +
        '<div class="wishlist-drawer__item-info">' +
          '<a class="wishlist-drawer__item-title" href="' + esc(url) + '">' + esc(it.title) + '</a>' +
          (kindLabel ? '<span class="wishlist-drawer__item-kind">' + esc(kindLabel) + '</span>' : '') +
          '<span class="wishlist-drawer__item-price">' + esc(price) + '</span>' +
        '</div>' +
        '<button type="button" class="wishlist-drawer__remove is-active" aria-label="Remove ' + esc(it.title) + '" aria-pressed="true"' +
          ' data-wishlist-btn data-list="' + list + '" data-product-id="' + esc(it.id) + '">' +
          '<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M15 5L5 15M5 5l10 10"/></svg>' +
        '</button>' +
      '</li>'
    );
  }

  // Placeholder rows while /state loads, so the drawer never opens empty
  // and then jumps. Same shape as a real row.
  function skeletonHtml(n) {
    var row = '<li class="wishlist-drawer__item wishlist-drawer__item--skel" aria-hidden="true">'
      + '<span class="wishlist-drawer__item-media"></span>'
      + '<span class="wishlist-drawer__skel-lines"><span></span><span></span></span><span></span></li>';
    return new Array(n + 1).join(row);
  }

  function renderSkeleton() {
    var d = drawerEl();
    if (!d) return;
    ['[data-wishlist-drawer-items]', '[data-reminders-drawer-items]'].forEach(function (sel) {
      var el = d.querySelector(sel);
      if (el && !el.children.length) el.innerHTML = skeletonHtml(3);
    });
    d.querySelectorAll('[data-wishlist-drawer-empty], [data-reminders-drawer-empty]').forEach(function (e) { e.hidden = true; });
  }

  // Collapse a row out: its height, padding and opacity go to zero, then it
  // is removed. Rows below slide up instead of jumping.
  function leaveRow(li) {
    if (li.classList.contains('is-leaving')) return;
    li.style.height = li.offsetHeight + 'px';
    void li.offsetHeight;
    li.classList.add('is-leaving');
    li.style.height = '0px';
    setTimeout(function () { if (li.parentNode) li.parentNode.removeChild(li); }, 320);
  }

  // Bring a list in line with `rows` ([{key, html}]) without rebuilding it:
  // rows already shown stay put, new ones rise in, gone ones collapse out.
  function patchList(el, rows) {
    if (!el) return;
    el.querySelectorAll('.wishlist-drawer__item--skel').forEach(function (li) { li.remove(); });
    var shown = {};
    el.querySelectorAll('.wishlist-drawer__item[data-key]:not(.is-leaving)').forEach(function (li) {
      shown[li.getAttribute('data-key')] = li;
    });
    var wanted = {};
    rows.forEach(function (r) { wanted[r.key] = true; });
    Object.keys(shown).forEach(function (k) { if (!wanted[k]) leaveRow(shown[k]); });
    // Rows already on screen never move (a collapsing row stays where it
    // was); a new row goes in just before the next row that is shown.
    var fresh = 0;
    var tmp = document.createElement('ul');
    rows.forEach(function (r, i) {
      if (shown[r.key]) return;
      tmp.innerHTML = r.html;
      var li = tmp.firstChild;
      li.style.animationDelay = Math.min(fresh++, 8) * 45 + 'ms';
      li.classList.add('is-entering');
      var before = null;
      for (var j = i + 1; j < rows.length && !before; j++) before = shown[rows[j].key] || null;
      el.insertBefore(li, before);
      shown[r.key] = li;
    });
  }

  function renderDrawer() {
    var d = drawerEl();
    if (!d || !state) return;
    var row = function (list, label) {
      return function (it) { return { key: list + ':' + it.id, html: itemHtml(it, list, label) }; };
    };
    var wishRows = (state.wishlist || []).map(row('wishlist'));
    var wlEmpty = d.querySelector('[data-wishlist-drawer-empty]');
    patchList(d.querySelector('[data-wishlist-drawer-items]'), wishRows);
    if (wlEmpty) wlEmpty.hidden = wishRows.length > 0;
    // Each reminder is its own row, labelled with what it waits for.
    var remRows = (state.stock || []).map(row('stock', "When it's back in stock"))
      .concat((state.sale || []).map(row('sale', "When it's on sale")))
      .concat((state.deal || []).map(row('deal', "When it's on sale")));
    var rmEmpty = d.querySelector('[data-reminders-drawer-empty]');
    patchList(d.querySelector('[data-reminders-drawer-items]'), remRows);
    if (rmEmpty) rmEmpty.hidden = remRows.length > 0;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      setCount(el, (state.wishlist || []).length);
    });
  }

  // A guest's drawer renders from the browser list at once, then takes current
  // titles, prices and images from the store (/products/<handle>.js), at most
  // once a minute. A product the store no longer shows leaves the list.
  function loadGuestDrawer(force) {
    state = guestState();
    renderDrawer();
    if (!force && Date.now() - stateAt < 60000) return;
    stateAt = Date.now();
    var items = state.wishlist.slice(0, 60);
    Promise.all(items.map(function (it) {
      if (!it.handle) return null;
      return fetch('/products/' + encodeURIComponent(it.handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.status === 404 ? { gone: true } : r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (found) {
      var byId = {};
      items.forEach(function (it, i) { byId[String(it.id)] = found[i]; });
      var next = [];
      readGuest().forEach(function (it) {
        var p = byId[String(it.id)];
        if (p && p.gone) return;
        if (p && p.id) {
          it.title = p.title || it.title;
          it.handle = p.handle || it.handle;
          if (p.featured_image) it.image = p.featured_image;
          if (isFinite(p.price_min)) it.price = money(p.price_min / 100);
        }
        next.push(it);
      });
      writeGuest(next);
      state = guestState();
      if (drawerOpen()) renderDrawer();
      paintGuest();
    });
  }

  function loadDrawer(force, opening) {
    if (!CFG.loggedIn) { loadGuestDrawer(force); return; }
    if (!force && state && Date.now() - stateAt < 60000) { renderDrawer(); return; }
    if (!state) renderSkeleton();
    fetch(PROXY_BASE + '/state', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (data) { state = data; stateAt = Date.now(); renderDrawer(); })
      .catch(function () { /* keep what is shown */ });
  }

  function setDrawerTab(tab) {
    var d = drawerEl();
    if (!d) return;
    d.querySelectorAll('[data-wishlist-tab]').forEach(function (b) {
      var on = b.getAttribute('data-wishlist-tab') === tab;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    d.querySelectorAll('[data-wishlist-panel]').forEach(function (p) {
      var on = p.getAttribute('data-wishlist-panel') === tab;
      p.hidden = !on;
      p.classList.toggle('is-active', on);
    });
  }

  function openDrawer(tab) {
    var d = drawerEl();
    if (!d) return;
    setDrawerTab(tab || 'wishlist');
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    loadDrawer(false, true);
  }

  function closeDrawer() {
    var d = drawerEl();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
  }

  function initDrawer() {
    document.querySelectorAll('[data-wishlist-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (drawerOpen()) closeDrawer(); else openDrawer('wishlist');
      });
    });
    var d = drawerEl();
    if (d) {
      d.querySelectorAll('[data-wishlist-tab]').forEach(function (b) {
        b.addEventListener('click', function () { setDrawerTab(b.getAttribute('data-wishlist-tab')); });
      });
      d.querySelectorAll('[data-wishlist-drawer-close]').forEach(function (el) {
        el.addEventListener('click', closeDrawer);
      });
    }
    // Desktop: a click outside the panel closes it.
    document.addEventListener('click', function (e) {
      if (!drawerOpen() || !window.matchMedia('(min-width: 750px)').matches) return;
      var panel = drawerEl().querySelector('.wishlist-drawer__panel');
      if (panel && panel.contains(e.target)) return;
      if (e.target.closest('[data-wishlist-open]')) return;
      closeDrawer();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeAllRemind(null); closeDrawer(); }
    });
  }

  /* ------------------------------ boot ------------------------------ */

  document.addEventListener('click', onClick, true);

  // ?reminders=open (the "Manage reminders" link in reminder emails) opens
  // the drawer on Reminders; guests log in first and land back here.
  function openFromLink() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('reminders') !== 'open') return;
    if (!CFG.loggedIn) { redirectToLogin(); return; }
    params.delete('reminders');
    var qs = params.toString();
    history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    openDrawer('reminders');
  }

  function boot() {
    // A fresh load is rendered from the account (Liquid), which is the truth,
    // also after changes made elsewhere (the account page, another device).
    // The click memory is only for back/forward restores (pageshow below).
    refreshRemindSet();
    layoutReminders();
    window.addEventListener('resize', layoutReminders);
    window.addEventListener('load', layoutReminders);
    initDrawer();
    openFromLink();
    finishIntent();
    paintGuest();
    watchGuestCards();
    mergeGuestList();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.addEventListener('pageshow', function (e) { if (e.persisted) reconcile(); });

  // A guest saving in another tab: this one follows.
  window.addEventListener('storage', function (e) {
    if (CFG.loggedIn || e.key !== GUEST_KEY) return;
    paintGuest();
    state = guestState();
    if (drawerOpen()) renderDrawer();
  });

  // Account page and drawer: fade out a row when its product is removed.
  document.addEventListener('wishlist:changed', function (e) {
    if (e.detail.active) return;
    var item = e.detail.source && e.detail.source.closest('[data-wishlist-grid-item]');
    if (!item) return;
    item.style.transition = 'opacity 0.2s ease';
    item.style.opacity = '0';
    setTimeout(function () { item.remove(); }, 200);
  });
})();
